import React from 'react';
import { useElapsedSeconds } from '../../hooks/useElapsedSeconds';
import { normalizeMatchStartTime } from '../../utils/matchTimerUtils';

/** Minutes elapsed before amber "long game" styling */
export const COURT_TIMER_AMBER_MINUTES = 15;
/** Minutes elapsed before stronger red "rotate soon" styling */
export const COURT_TIMER_WARNING_MINUTES = 20;

/**
 * Live timer — shows how long a court game has been running.
 * @param {Date|string|null} startTime - When the game started (ISO preferred)
 * @param {boolean} isInProgress - Whether the game is currently in progress
 */
const CourtTimer = ({ startTime, isInProgress }) => {
  const normalizedStart = normalizeMatchStartTime(startTime);
  const { elapsedSeconds, formattedMmSs } = useElapsedSeconds(
    normalizedStart,
    isInProgress
  );

  if (!isInProgress || !normalizedStart) {
    return null;
  }

  const elapsedMinutes = elapsedSeconds / 60;
  const isWarning = elapsedMinutes >= COURT_TIMER_WARNING_MINUTES;
  const isLong = elapsedMinutes >= COURT_TIMER_AMBER_MINUTES && !isWarning;

  const dotClass = isWarning
    ? 'bg-danger'
    : isLong
      ? 'bg-warning'
      : 'bg-danger';
  const pingClass = isWarning
    ? 'bg-danger'
    : isLong
      ? 'bg-warning'
      : 'bg-danger';
  const textClass = isWarning
    ? 'text-danger'
    : isLong
      ? 'text-warning'
      : 'text-text-primary';

  const minutes = Math.floor(elapsedSeconds / 60);
  const ariaLabel =
    minutes === 0
      ? `Match duration ${elapsedSeconds} seconds`
      : `Match duration ${minutes} minute${minutes === 1 ? '' : 's'} ${elapsedSeconds % 60} seconds`;

  return (
    <div
      className="flex items-center gap-1.5 text-sm"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      <div className="flex items-center gap-1">
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full ${pingClass} opacity-75`}
          />
          <span
            className={`relative inline-flex rounded-full h-2 w-2 ${dotClass}`}
          />
        </span>
        <span
          className={`font-mono font-semibold tabular-nums ${textClass}`}
        >
          {formattedMmSs}
        </span>
      </div>
    </div>
  );
};

export default CourtTimer;
